import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import {
  fetchReportWidget,
  selectReportingV2Filters,
  selectReportingV2Widget,
  setTrendLiveRows,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { LightbulbOutlined } from '@mui/icons-material';
// `Tooltip` is taken by the chart.js import below, hence the alias.
import {
  Box,
  Button,
  Chip,
  Tooltip as MuiTooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { CategoryScale, Chart as ChartJS, Filler, LineElement, LinearScale, PointElement, Tooltip } from 'chart.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  type ChannelOption,
  FREQUENCY_LABEL,
  FREQUENCY_NOTE,
  FREQUENCY_UNAVAILABLE_NOTE,
  decimal,
  genericChannelLabel,
  isFrequencyComparable,
  mergeChannelOptionsForRestrictedViewer,
  num,
} from '../format';
import { liveCovers, liveReachKey, useDv360LiveReach } from '../hooks/useDv360LiveReach';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import useFilterOptions from '../hooks/useFilterOptions';
import { shiftBack } from '../hooks/usePriorPeriod';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { COLORS } from '../theme';
import { buildRequest } from '../widgets';
import ColumnResizeHandle from './ColumnResizeHandle';
import { toggleChannelSelection } from './ReportingV2FilterBar';
import WidgetExportButton from './WidgetExportButton';
import WidgetSkeleton from './WidgetSkeleton';
import { CHANNEL_COLORS, colorFor } from './palette';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip);

const WIDGET_KEY = 'trend';
const PRIOR_KEY = 'trendPrior';
const CHANNEL_KEY = 'trendByChannel';
const BUCKETS = ['Day', 'Week', 'Month'] as const;
type Bucket = (typeof BUCKETS)[number];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type TrendRow = {
  day?: unknown;
  impressions?: unknown;
  reach?: unknown;
  channel?: unknown;
  /** A delivery day live DV360 reach doesn't cover (flank#4479): its whole bucket is withheld. */
  liveUncovered?: boolean;
};

/** The bucket key + axis label a given calendar day belongs to. */
const bucketOf = (ymd: string, bucket: Bucket): { key: string; label: string } => {
  const d = new Date(`${ymd}T00:00:00`);
  if (bucket === 'Month') {
    return { key: ymd.slice(0, 7), label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` };
  }
  if (bucket === 'Week') {
    const offset = (d.getDay() + 6) % 7;
    const start = new Date(d);
    start.setDate(d.getDate() - offset);
    const startYmd = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(
      start.getDate(),
    ).padStart(2, '0')}`;
    return { key: startYmd, label: `${MONTHS[start.getMonth()]} ${start.getDate()}` };
  }
  return { key: ymd, label: `${MONTHS[d.getMonth()]} ${d.getDate()}` };
};

/** Days from `from` to `to`, inclusive of neither end — just the offset. */
const dayOffset = (from: string, to: string): number =>
  Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86_400_000);

/** Same calendar day shifted forward by `days`. */
const shiftYmd = (ymd: string, days: number): string => {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Bucket the trend rows onto a DENSE axis spanning the whole selected window.
 *
 * Previously this returned only the buckets the API actually returned, which
 * made the chart silently lie about its own range: with data ending 24 Jul, a
 * "Last 90 Days" window (3 May - 31 Jul) drew a line that appeared to start in
 * June, and the points bunched toward one side because the axis had no slots
 * for the missing days. Testers read both as chart bugs.
 *
 * Generating every bucket in [start, end] and defaulting to 0 matches the
 * reference implementation and makes a gap in the data look like a gap.
 */
const bucketize = (rows: TrendRow[], bucket: Bucket, start: string, end: string) => {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const ymd = String(row.day ?? '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) continue;
    const { key } = bucketOf(ymd, bucket);
    totals.set(key, (totals.get(key) ?? 0) + Number(row.impressions ?? 0));
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return [];

  const out: { label: string; value: number }[] = [];
  const seen = new Set<string>();
  const cursor = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  // Walk day by day so week/month buckets fall out of the same code path.
  while (cursor <= last) {
    const ymd = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(
      cursor.getDate(),
    ).padStart(2, '0')}`;
    const { key, label } = bucketOf(ymd, bucket);
    if (!seen.has(key)) {
      seen.add(key);
      out.push({ label, value: totals.get(key) ?? 0 });
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  // Drop trailing empty buckets. The window runs to today but ingest lands a day
  // or two behind, so zero-filling to the end drew a cliff to zero and read as
  // delivery stopping. Interior zeros are kept — a real gap mid-flight is a fact.
  let lastWithData = out.length;
  while (lastWithData > 0 && out[lastWithData - 1].value === 0) lastWithData--;
  return out.slice(0, lastWithData);
};

/**
 * Frequency per bucket, as the MEAN of the daily frequencies inside it.
 *
 * Deliberately not sum(impressions) / sum(reach): canonical's reach aggregates
 * additively across days, so that form divides by a denominator counting the
 * same person once per day they were reached, and the answer sags further the
 * wider the bucket. Averaging the daily values keeps a Week and a Month bar
 * meaning the same thing a Day bar does — which is what the axis label claims
 * (see FREQUENCY_NOTE).
 *
 * Days with no reach contribute nothing rather than a zero: GAM reports reach 0
 * on every row (ingestion#230) and Google/Bing report NULL, so counting those as
 * frequency 0 would drag every bucket toward zero for reasons that have nothing
 * to do with delivery.
 */
export const bucketizeFrequency = (rows: TrendRow[], bucket: Bucket, start: string, end: string) => {
  const acc = new Map<string, { sum: number; n: number }>();
  const withheld = new Set<string>();
  for (const row of rows) {
    const ymd = String(row.day ?? '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) continue;
    if (row.liveUncovered) withheld.add(bucketOf(ymd, bucket).key);
    const impressions = Number(row.impressions ?? 0);
    const reach = Number(row.reach ?? 0);
    if (!(reach > 0) || !(impressions > 0)) continue;
    const { key } = bucketOf(ymd, bucket);
    const entry = acc.get(key) ?? { sum: 0, n: 0 };
    entry.sum += impressions / reach;
    entry.n += 1;
    acc.set(key, entry);
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return [];

  const out: (number | null)[] = [];
  const seen = new Set<string>();
  const cursor = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  while (cursor <= last) {
    const ymd = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(
      cursor.getDate(),
    ).padStart(2, '0')}`;
    const { key } = bucketOf(ymd, bucket);
    if (!seen.has(key)) {
      seen.add(key);
      const entry = acc.get(key);
      // null, not 0 — chart.js breaks the line rather than drawing a dive to
      // zero through a period nothing reported reach for.
      out.push(entry && entry.n > 0 && !withheld.has(key) ? entry.sum / entry.n : null);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

/** Wording for the insight line — it describes the *selected* bucket, not always days. */
const AVERAGE_LABEL: Record<Bucket, string> = { Day: 'daily', Week: 'weekly', Month: 'monthly' };

/** Returns a short auto-generated insight string for the visible data window. */
const buildInsight = (series: { label: string; value: number }[], bucket: Bucket): string | null => {
  if (series.length < 2) return null;
  const max = series.reduce((best, p) => (p.value > best.value ? p : best), series[0]);
  // Average over buckets that actually carry data. The axis is zero-filled
  // across the whole window, so including empty buckets would divide by a run
  // of zeros where ingest simply hasn't reached — inflating "% above average"
  // purely because the window extends past the data.
  const withData = series.filter(p => p.value > 0);
  if (withData.length < 2) return null;
  const avg = withData.reduce((s, p) => s + p.value, 0) / withData.length;
  if (avg === 0) return null;
  const pct = Math.round(((max.value - avg) / avg) * 100);
  if (pct < 10) return null;
  return `Impressions peaked ${max.label} (${pct}% above the ${AVERAGE_LABEL[bucket]} average).`;
};

/** The main total dataset's label — "Total in filter" while comparing channels
 * so the tooltip and the table read as a filtered total next to per-channel
 * lines, rather than still claiming to be "Impressions" overall. */
export const totalSeriesLabel = (comparing: boolean): string => (comparing ? 'Total in filter' : 'Impressions');

/**
 * One color per channel VALUE, keyed by its index in the full, stable
 * `options` list (the page header's Channel filter, flank#4627) — not by a
 * channel's index in whatever subset is currently selected to compare.
 *
 * Review, Lucas (flank#4651): the chip picked its SERIES fallback by index in
 * `channelFilterOptions.options`, the chart line by index in `selectedChannels`
 * — a different, reordering/shrinking list — so an unmapped channel's chip
 * border and line color didn't match. `+ 1` skips `SERIES[0]`
 * (`COLORS.primary`), the total line's own color; with the legend hidden the
 * chip is the user's only key to which line is which.
 */
export const colorForChannel = (value: string, options: { value: string }[]): string => {
  const index = options.findIndex(o => o.value === value);
  return colorFor(value, CHANNEL_COLORS, (index < 0 ? 0 : index) + 1);
};

/**
 * Per-channel bucketized series, built from the exact same `bucketize` the
 * total line already uses (just pre-filtered to one channel's rows) — so a
 * channel's chart line can never drift from what the total itself would
 * compute for that subset of rows.
 */
export const buildChannelSeriesMap = (
  rows: TrendRow[],
  channels: string[],
  bucket: Bucket,
  start: string,
  end: string,
): Record<string, { label: string; value: number }[]> => {
  const out: Record<string, { label: string; value: number }[]> = {};
  for (const channel of channels) {
    out[channel] = bucketize(
      rows.filter(row => String(row.channel ?? '') === channel),
      bucket,
      start,
      end,
    );
  }
  return out;
};

/**
 * Aligns a (possibly shorter, trailing-zero-trimmed — see `bucketize`) series
 * onto the chart's own label axis so every line and table row shares the same
 * x-axis indices. A channel whose own trailing days are empty trims shorter
 * than the total and must not shift out of step with it; the missing tail is
 * genuinely zero for that channel, not unknown.
 */
export const alignSeries = (series: { label: string; value: number }[], length: number): number[] =>
  Array.from({ length }, (_, i) => series[i]?.value ?? 0);

/**
 * Sums several already-aligned series index-by-index — how a restricted
 * viewer's merged channel option (e.g. "Display" covering both a true
 * "Display" channel and GAM's "GAM Display") becomes ONE chart line/table row
 * instead of two, now that `selectedChannels` can hold more than one raw
 * value for a single merged chip (flank#4741 QA, Compare Channels).
 */
export const sumAlignedSeries = (seriesList: number[][], length: number): number[] =>
  Array.from({ length }, (_, i) => seriesList.reduce((sum, s) => sum + (s[i] ?? 0), 0));

/**
 * Collapses the selected RAW channels onto the merged chip groups a
 * restricted viewer sees (`channelChipOptions`, from
 * `mergeChannelOptionsForRestrictedViewer`), summing each group's channels
 * into one series — one chart line / tooltip row / table row per chip, not
 * one per raw value. Without this, a restricted viewer whose single "Display"
 * chip pick carries both a true "Display" channel and GAM's "GAM Display"
 * (the exact collision the chip merge fixes) would still see the chart,
 * tooltip and table draw it as two identically-labeled "Display" series
 * (flank#4741 QA). For an unrestricted viewer every option is its own
 * singleton group, so this is a no-op reshape of the previous one-per-channel
 * behavior.
 */
export const buildChannelCompareSeries = (
  channelChipOptions: readonly ChannelOption[],
  selectedChannels: string[],
  channelSeriesMap: Record<string, { label: string; value: number }[]>,
  length: number,
  canViewPlatform: boolean,
): { name: string; colorKey: string; values: number[] }[] =>
  channelChipOptions
    .map(opt => ({ opt, selected: opt.values.filter(v => selectedChannels.includes(v)) }))
    .filter(({ selected }) => selected.length > 0)
    .map(({ opt, selected }) => ({
      // Display name only — `colorKey` below keeps a raw value so
      // `channelColor` still indexes by the same stable option list
      // regardless of what's shown on screen (flank#4741).
      name: genericChannelLabel(opt.label, canViewPlatform),
      colorKey: selected[0],
      values: sumAlignedSeries(
        selected.map(channel => alignSeries(channelSeriesMap[channel] ?? [], length)),
        length,
      ),
    }));

export const TABLE_TRUNCATION_NOTE = 'First 8 days shown. Switch to Week or Month to see the whole period.';

// Day x Channel can still exceed ribeye-api's 1000-row cap with several
// channels selected over a long window, even after scoping the fetch to just
// the active chips (review, Lucas — see the compare-channels effect).
export const CHANNEL_TRUNCATION_NOTE =
  'Comparison may be incomplete for this date range — narrow the window or compare fewer channels.';

const TABLE_ELEMENT_ID = 'impressions-over-time-table';

/** Clips a date axis to its first 8 entries on Day granularity when the period
 * runs longer than that; Week and Month always show the whole period. */
export const truncateForDayTable = <T,>(labels: T[], bucket: Bucket): { labels: T[]; truncated: boolean } => {
  if (bucket !== 'Day' || labels.length <= 8) return { labels, truncated: false };
  return { labels: labels.slice(0, 8), truncated: true };
};

export interface TrendTableRow {
  name: string;
  cells: number[];
  periodTotal: number;
}

/**
 * Builds the "View as table" grid from the SAME aligned, per-row series the
 * chart already renders (`alignSeries`/`buildChannelSeriesMap` above) — never
 * a separate aggregation, so the grid's numbers can't silently drift from the
 * chart or its tooltip. The Period total column sums the whole period even
 * when the date columns themselves are truncated to the first 8 days.
 */
export const buildTrendTable = (
  entries: { name: string; values: number[] }[],
  labels: string[],
  bucket: Bucket,
): { dateLabels: string[]; rows: TrendTableRow[]; truncated: boolean } => {
  const { labels: dateLabels, truncated } = truncateForDayTable(labels, bucket);
  const rows = entries.map(entry => ({
    name: entry.name,
    cells: dateLabels.map((_, i) => entry.values[i] ?? 0),
    periodTotal: entry.values.reduce((sum, v) => sum + v, 0),
  }));
  return { dateLabels, rows, truncated };
};

const linkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.4,
  cursor: 'pointer',
  color: COLORS.muted,
  fontWeight: 500,
  fontSize: '0.8rem',
  textTransform: 'none',
  userSelect: 'none',
  '&:hover': { color: COLORS.text },
} as const;

const compareChipSx = (active: boolean) =>
  ({
    height: 28,
    fontWeight: active ? 600 : 400,
    fontSize: '0.78rem',
    bgcolor: active ? COLORS.primary : 'transparent',
    color: active ? '#fff' : COLORS.text,
    border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
    borderRadius: '6px',
    '& .MuiChip-label': { px: 1.25 },
    '&:hover': { bgcolor: active ? COLORS.primaryHover : COLORS.pageBg },
  }) as const;

const compareSectionLabelSx = {
  color: COLORS.muted,
  fontWeight: 700,
  fontSize: '0.7rem',
  letterSpacing: 0.5,
  minWidth: 120,
  flexShrink: 0,
  alignSelf: 'center',
} as const;

const ImpressionsOverTime = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectReportingV2Filters);
  const { rows, loading, error } = useAppSelector(selectReportingV2Widget(WIDGET_KEY));
  const { rows: priorRows } = useAppSelector(selectReportingV2Widget(PRIOR_KEY));
  const {
    rows: channelRows,
    totalRows: channelTotalRows,
    loading: channelLoading,
    error: channelError,
  } = useAppSelector(selectReportingV2Widget(CHANNEL_KEY));
  const onExportCsv = useExportWidgetCsv('trend', rows);
  const [bucket, setBucket] = useState<Bucket>('Day');
  const [overlay, setOverlay] = useState<'none' | 'frequency'>('none');
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [showTable, setShowTable] = useState(false);
  const { widths: colWidths, startResize } = useResizableColumns('impressionsOverTime');
  // The live, fact-backed channel list — same source the page header's
  // Channel filter chips use (flank#4627), never a hardcoded list.
  const channelFilterOptions = useFilterOptions('channel');
  // GAM's channel taxonomy bakes the platform name into the value itself
  // ("GAM Display"/"GAM Video") where every other DSP's same channel type
  // reads bare — a second DSP-identifying leak alongside the Platform chip
  // (flank#4741 QA) for a user without canViewPlatform. Applied to displayed
  // text only below; `opt.value`/`channel` keep flowing as the raw filter and
  // series key so selection, coloring and the request itself are unaffected.
  const { canViewPlatform } = useReportingPermissions();
  // Same collision `mergeChannelOptionsForRestrictedViewer` fixes on the page
  // header's Channel filter (flank#4741 QA): for a restricted viewer, a true
  // "Display" channel and GAM's "GAM Display" both mask to "Display". That
  // filter bar already pushes BOTH raw values into `filters.channelIds`/the
  // Compare Channels selection for one merged pick, so this chip row and the
  // chart below must group them back into one option/series rather than
  // rendering (or plotting) the collision twice.
  const channelChipOptions = useMemo(
    () => mergeChannelOptionsForRestrictedViewer(channelFilterOptions.options, canViewPlatform),
    [channelFilterOptions.options, canViewPlatform],
  );
  const hasChannelFilter = (filters.channelIds ?? []).length > 0;
  // A page-level filter on more than one channel sums all of them (buildRequest
  // forwards the whole list), so the row must name every one of them rather
  // than just the first id or a vague count (review, Lucas: "CTV" on a
  // CTV+Display filter understated what it totals; "2 channels" didn't say which).
  // De-duped (Set) because a restricted viewer's merged Channel-filter pick puts
  // both raw values behind a collision into `channelIds`, which would otherwise
  // join back together as "Display + Display".
  const activeChannelLabel = hasChannelFilter
    ? Array.from(
        new Set(
          filters.channelIds.map(id =>
            genericChannelLabel(channelFilterOptions.options.find(o => o.value === id)?.label ?? id, canViewPlatform),
          ),
        ),
      ).join(' + ')
    : null;
  const comparing = !hasChannelFilter && selectedChannels.length > 0;
  // Comparing fetches CHANNEL_KEY separately from the total (see effect below);
  // treat its own loading/error like the total's so a still-loading or failed
  // channel request can't render as zero-valued comparison lines/rows.
  //
  // `channelLoading` alone isn't enough (review, Lucas): the fetch for a NEW
  // selection is dispatched in an effect that runs after this render, and on
  // filter changes the widget can still be holding the PREVIOUS filters' rows
  // with `loading: false`. `channelsReqSignatureRef` records which
  // (filters, selectedChannels) pair the effect has actually dispatched for;
  // until it matches the signature computed on THIS render, treat it as
  // pending too, hiding whatever stale rows are still in the store.
  const channelsSignature = comparing ? JSON.stringify({ filters, selectedChannels }) : null;
  const channelsReqSignatureRef = useRef<string | null>(null);
  const channelsPending = comparing && (channelLoading || channelsReqSignatureRef.current !== channelsSignature);
  const channelsFailed = comparing && Boolean(channelError) && channelsReqSignatureRef.current === channelsSignature;
  // Day x Channel can exceed ribeye-api's 1000-row cap over a long window with
  // several channels selected (review, Lucas: a prior 10,000-row request was
  // simply rejected by the validator). Scoping the fetch to only the selected
  // channels (not every channel) keeps this well under the cap for the common
  // case; `channelsTruncated` still surfaces the rare case where it isn't.
  const channelsTruncated = comparing && !channelsPending && channelRows.length < channelTotalRows;
  const channelColor = (value: string): string => colorForChannel(value, channelFilterOptions.options);

  // A page-level Channel filter narrows the chart to one channel and hides
  // this row entirely (AC) — clear any stale local selection so it can't
  // resurface with the same channels once the filter is removed.
  useEffect(() => {
    if (hasChannelFilter) setSelectedChannels([]);
  }, [hasChannelFilter]);

  // Same merge-aware toggle as the filter bar's `toggleChannel`
  // (ReportingV2FilterBar.tsx) — a merged chip's `opt.value` resolves to its
  // whole raw-value group, so selecting/deselecting it adds/removes both
  // "Display" and "GAM Display" together rather than just whichever one the
  // click target happened to be.
  const toggleCompareChannel = (channel: string) => {
    setSelectedChannels(current => toggleChannelSelection(current, channel, channelChipOptions));
  };

  useEffect(() => {
    dispatch(
      fetchReportWidget({
        key: WIDGET_KEY,
        request: buildRequest(
          {
            key: WIDGET_KEY,
            title: '',
            dimensions: ['day'],
            // reach rides along for the frequency overlay — same rows, no extra query.
            metrics: ['impressions', 'reach'],
            sort: [{ field: 'day', dir: 'asc' }],
          },
          filters,
        ),
      }),
    );
    // Prior period — same shape, shifted dates
    const { start, end } = shiftBack(filters.dateStart, filters.dateEnd);
    dispatch(
      fetchReportWidget({
        key: PRIOR_KEY,
        request: buildRequest(
          {
            key: PRIOR_KEY,
            title: '',
            dimensions: ['day'],
            metrics: ['impressions'],
            sort: [{ field: 'day', dir: 'asc' }],
          },
          { ...filters, dateStart: start, dateEnd: end },
        ),
      }),
    );
  }, [dispatch, filters]);

  // Compare channels (#4637) — refetched on every selection/filter change,
  // scoped to only the selected channels via `channel_ids`.
  //
  // review, Lucas: the previous version fetched every channel at once with
  // `limit: 10_000` so toggling chips was free — ribeye-api rejects any limit
  // over 1000 (`ReportValidator.ts`), so that request always 400'd and comparing
  // never worked. Filtering to just the active chips instead of every channel
  // keeps a typical comparison (a handful of channels) well under the cap;
  // `channelsTruncated` above still tells the user when even that isn't enough.
  useEffect(() => {
    if (!comparing) return;
    channelsReqSignatureRef.current = channelsSignature;
    dispatch(
      fetchReportWidget({
        key: CHANNEL_KEY,
        request: buildRequest(
          {
            key: CHANNEL_KEY,
            title: '',
            dimensions: ['day', 'channel'],
            metrics: ['impressions'],
            sort: [{ field: 'day', dir: 'asc' }],
            limit: 1000,
          },
          { ...filters, channelIds: selectedChannels },
        ),
      }),
    );
    // `channelsSignature` already encodes both `filters` and `selectedChannels`.
  }, [dispatch, comparing, channelsSignature]);

  const series = bucketize(rows as TrendRow[], bucket, filters.dateStart, filters.dateEnd);
  // Frequency can't be blended across platforms (see isFrequencyComparable).
  const frequencyComparable = isFrequencyComparable(filters.dsps);
  // DV360-only: per-day reach straight from DV360 instead of the summed canonical reach (flank#4479).
  const liveDayReach = useDv360LiveReach('day', { enabled: overlay === 'frequency' });
  // Memoized: the effect below dispatches these rows, and a new array on every render would
  // re-dispatch forever (React #185 with the overlay on).
  const frequencyRows = useMemo(
    () =>
      liveDayReach.active
        ? (rows as TrendRow[]).map(row => {
            const live = liveDayReach.byKey.get(liveReachKey('day', row.day));
            const covered = liveCovers(row.impressions, live);
            return {
              ...row,
              reach: covered ? (live?.reach ?? null) : null,
              // Uncovered, or covered but the platform withheld the day's reach: either way the bucket is incomplete.
              liveUncovered: Number(row.impressions ?? 0) > 0 && (!covered || live?.reach == null),
            };
          })
        : (rows as TrendRow[]),
    [liveDayReach.active, liveDayReach.byKey, rows],
  );
  const frequencySeries =
    overlay === 'frequency' && frequencyComparable
      ? bucketizeFrequency(frequencyRows, bucket, filters.dateStart, filters.dateEnd).slice(0, series.length)
      : [];
  // The page-level Export dialog reads trend rows from the store; give it the
  // same per-day live reach the frequency overlay shows (plus the loading
  // flag, so an export mid-fetch waits instead of silently getting the
  // pre-live summed reach — CodeRabbit, flank#5188), or it silently exports
  // the pre-live summed canonical reach (CodeRabbit / preprod QA, flank#4479).
  // Only ever set while the overlay is on, since that's the only thing that
  // fetches per-day live reach in the first place — a DV360-only export with
  // the overlay off still exports summed reach here even though Key Metrics
  // above it is live, a known gap accepted on flank#4479 rather than fetching
  // live reach the export dialog never asked for.
  // `liveUncovered` is a chart-only flag, not a real column — stripped so it
  // can't leak into the CSV/Excel export as a stray field.
  //
  // Depends on `rows`/`liveDayReach.byKey` (stable references until the data
  // itself changes), not the derived `frequencyRows` — that's a brand-new
  // array on every render, so putting it in the dependency list re-fired this
  // effect every render, which re-dispatched every render, which (via
  // selectReportingV2AnyLoading) re-rendered this component, forever —
  // React error #185, found live on preprod with the frequency overlay on.
  useEffect(() => {
    dispatch(
      setTrendLiveRows({
        rows:
          liveDayReach.active && !liveDayReach.loading
            ? (rows as TrendRow[]).map(row => {
                const live = liveDayReach.byKey.get(liveReachKey('day', row.day));
                const covered = liveCovers(row.impressions, live);
                return { ...row, reach: covered ? (live?.reach ?? null) : null } as unknown as ReportRow;
              })
            : null,
        loading: liveDayReach.active && liveDayReach.loading,
      }),
    );
  }, [dispatch, rows, liveDayReach.active, liveDayReach.loading, liveDayReach.byKey]);
  useEffect(() => () => void dispatch(setTrendLiveRows({ rows: null, loading: false })), [dispatch]);
  const hasFrequency = frequencySeries.some(v => v !== null);
  const liveFrequencyNote =
    overlay !== 'frequency' || !liveDayReach.active
      ? null
      : liveDayReach.loading
        ? `Loading ${liveDayReach.label} reach…`
        : liveDayReach.failed
          ? liveDayReach.notes.failed
          : !hasFrequency
            ? liveDayReach.notes.partial
            : null;

  /*
   * Re-index the prior period onto the CURRENT window's bucket boundaries.
   *
   * Bucketing each window independently does NOT line up: week and month
   * buckets start on calendar boundaries, so a shifted window can produce a
   * different bucket COUNT (e.g. 2025-12-09..2026-01-07 gives 5 week buckets
   * against its prior window's 6, silently dropping the tail), and even at
   * equal counts the first and last buckets cover different numbers of days.
   * Shifting each prior row forward by the window length first means both
   * series are bucketed by the same boundaries, so index i really is comparable.
   */
  const priorWindow = shiftBack(filters.dateStart, filters.dateEnd);
  const shiftDays = dayOffset(priorWindow.start, filters.dateStart);
  const priorShifted = (priorRows as TrendRow[]).map(row => {
    const ymd = String(row.day ?? '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? { ...row, day: shiftYmd(ymd, shiftDays) } : row;
  });
  const priorSeries = bucketize(priorShifted, bucket, filters.dateStart, filters.dateEnd);
  const labels = series.map(p => p.label);
  const values = series.map(p => p.value);
  // Align prior values to current series length (pad/trim)
  const priorValues = series.map((_, i) => priorSeries[i]?.value ?? null);
  const insight = buildInsight(series, bucket);

  // Compare channels — one aligned number[] per selected channel, derived from
  // the same `bucketize` call the total line uses (see `buildChannelSeriesMap`).
  const channelSeriesMap = comparing
    ? buildChannelSeriesMap(channelRows as TrendRow[], selectedChannels, bucket, filters.dateStart, filters.dateEnd)
    : {};
  // Grouped by merged chip (flank#4741 QA), not by raw channel — see
  // `buildChannelCompareSeries`.
  const channelValuesByName = buildChannelCompareSeries(
    channelChipOptions,
    selectedChannels,
    channelSeriesMap,
    labels.length,
    canViewPlatform,
  );
  const totalLabel = totalSeriesLabel(comparing);

  // View as table — reuses whichever series the chart is already showing, so
  // the grid can never recompute a different number than the chart/tooltip.
  // While comparing, the chart draws `totalLabel` ("Total in filter") as its
  // first line and the tooltip lists it alongside each channel — the table
  // must match both exactly, not just show the per-channel breakdown (review,
  // Lucas).
  const tableEntries = hasChannelFilter
    ? [{ name: activeChannelLabel as string, values }]
    : comparing
      ? [{ name: totalLabel, values }, ...channelValuesByName]
      : [{ name: 'Total', values }];
  const table = buildTrendTable(tableEntries, labels, bucket);

  return (
    <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }} elevation={0} variant='outlined'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
            Impressions Over Time
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant='caption' sx={{ color: COLORS.muted, fontWeight: 600, letterSpacing: 0.3 }}>
              OVERLAY
            </Typography>
            {/*
             * Store Visits / Web Conversions removed per #4453 — low adoption,
             * high complexity, and both depended on attribution data that was
             * never wired up (they were permanently disabled here). Insights
             * keeps both metrics; only this chart's overlay control drops them.
             */}
            <ToggleButtonGroup
              size='small'
              exclusive
              value={overlay}
              onChange={(_event, next) => next && setOverlay(next)}
            >
              {[
                { value: 'none', label: 'None', disabled: false },
                { value: 'frequency', label: FREQUENCY_LABEL, disabled: !frequencyComparable },
              ].map(o => {
                const button = (
                  <ToggleButton
                    key={o.value}
                    value={o.value}
                    disabled={o.disabled}
                    sx={{ textTransform: 'none', px: 1.25, py: 0.25, fontSize: '0.75rem' }}
                  >
                    {o.label}
                  </ToggleButton>
                );
                if (o.value !== 'frequency') return button;
                return (
                  <MuiTooltip
                    key={o.value}
                    title={o.disabled ? FREQUENCY_UNAVAILABLE_NOTE : FREQUENCY_NOTE}
                    placement='top'
                    arrow
                  >
                    <Box sx={{ display: 'inline-flex' }}>{button}</Box>
                  </MuiTooltip>
                );
              })}
            </ToggleButtonGroup>
            {liveFrequencyNote && (
              <Typography variant='caption' sx={{ color: COLORS.muted }}>
                {liveFrequencyNote}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup size='small' exclusive value={bucket} onChange={(_event, next) => next && setBucket(next)}>
            {BUCKETS.map(b => (
              <ToggleButton key={b} value={b} sx={{ textTransform: 'none' }}>
                {b}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            disableRipple
            onClick={() => setShowTable(v => !v)}
            aria-expanded={showTable}
            aria-controls={TABLE_ELEMENT_ID}
            sx={{ ...linkSx, minWidth: 0, p: 0, '&:hover': { ...linkSx['&:hover'], bgcolor: 'transparent' } }}
          >
            {showTable ? 'Hide table' : 'View as table'}
          </Button>
          <WidgetExportButton onExport={onExportCsv} disabled={rows.length === 0} />
        </Box>
      </Box>

      {!hasChannelFilter && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
          <Typography variant='overline' sx={compareSectionLabelSx}>
            Compare channels
          </Typography>
          {channelChipOptions.map(opt => {
            // `opt.values` is the whole raw-channel group behind this chip —
            // a singleton everywhere except a restricted viewer's merged
            // option (flank#4741 QA) — so "active" and its color both read
            // off the group, not just `opt.value` (which for a merged option
            // is the masked label, not a real channel).
            const active = opt.values.some(v => selectedChannels.includes(v));
            return (
              <Chip
                key={opt.value}
                label={genericChannelLabel(opt.label, canViewPlatform)}
                size='small'
                clickable
                aria-pressed={active}
                onClick={() => toggleCompareChannel(opt.value)}
                sx={{
                  ...compareChipSx(active),
                  ...(active ? { borderColor: channelColor(opt.values[0]) } : {}),
                }}
              />
            );
          })}
          {selectedChannels.length > 0 && (
            <Chip label='Clear' size='small' clickable variant='outlined' onClick={() => setSelectedChannels([])} />
          )}
        </Box>
      )}

      {comparing && (
        <Typography variant='caption' sx={{ color: COLORS.muted }}>
          Prior period hidden while comparing channels.
        </Typography>
      )}

      {channelsTruncated && (
        <Typography variant='caption' sx={{ color: COLORS.muted }}>
          {CHANNEL_TRUNCATION_NOTE}
        </Typography>
      )}

      {insight && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            px: 2,
            py: 1.25,
            borderRadius: '8px',
            bgcolor: '#FFFBEB',
            border: '1px solid #FDE68A',
          }}
        >
          <LightbulbOutlined sx={{ fontSize: 16, color: '#D97706', mt: 0.1, flexShrink: 0 }} />
          <Typography variant='body2' sx={{ color: '#92400E' }}>
            <strong>Insight:</strong> {insight}
          </Typography>
        </Box>
      )}

      <Box sx={{ height: 300 }}>
        {loading || channelsPending ? (
          <WidgetSkeleton height={300} />
        ) : error ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body2' color='error'>
              {error}
            </Typography>
          </Box>
        ) : channelsFailed ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body2' color='error'>
              {channelError}
            </Typography>
          </Box>
        ) : series.length === 0 ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              No impressions data for the selected filters.
            </Typography>
          </Box>
        ) : (
          <Line
            data={{
              labels,
              datasets: [
                {
                  label: totalLabel,
                  data: values,
                  borderColor: COLORS.primary,
                  borderWidth: 2,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  tension: 0.35,
                  fill: true,
                  backgroundColor: ctx => {
                    const { ctx: canvas, chartArea } = ctx.chart;
                    if (!chartArea) return `${COLORS.primary}1e`;
                    const gradient = canvas.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, `${COLORS.primary}2e`);
                    gradient.addColorStop(1, `${COLORS.primary}00`);
                    return gradient;
                  },
                },
                // Compare channels (#4637) — one thin line per selected channel,
                // on the same scale as the total. Colors match the chip (both
                // come from `channelColor`, keyed by the same stable option index).
                ...channelValuesByName.map(c => ({
                  label: c.name,
                  data: c.values,
                  borderColor: channelColor(c.colorKey),
                  borderWidth: 1.5,
                  pointRadius: 0,
                  pointHoverRadius: 3,
                  tension: 0.35,
                  fill: false,
                  backgroundColor: 'transparent',
                })),
                ...(hasFrequency
                  ? [
                      {
                        label: FREQUENCY_LABEL,
                        data: frequencySeries,
                        // Own axis: frequency is single digits against millions
                        // of impressions, so on a shared scale it draws as a
                        // flat line along the floor.
                        yAxisID: 'y1',
                        borderColor: COLORS.black,
                        borderWidth: 1.5,
                        pointRadius: 0,
                        pointHoverRadius: 4,
                        tension: 0.35,
                        fill: false,
                        backgroundColor: 'transparent',
                        // Leave real gaps where no source reported reach rather
                        // than joining across them.
                        spanGaps: false,
                      },
                    ]
                  : []),
                ...(!comparing && priorValues.some(v => v !== null)
                  ? [
                      {
                        label: 'Prior period',
                        data: priorValues,
                        borderColor: '#9CA3AF',
                        borderWidth: 1.5,
                        borderDash: [4, 4],
                        pointRadius: 0,
                        pointHoverRadius: 3,
                        tension: 0.35,
                        fill: false,
                        backgroundColor: 'transparent',
                      },
                    ]
                  : []),
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: {
                datalabels: { display: false },
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: context => `${context.dataset.label}: ${num(context.parsed.y)}`,
                  },
                },
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: {
                    color: COLORS.muted,
                    autoSkip: true,
                    maxTicksLimit: 8,
                    maxRotation: 0,
                    minRotation: 0,
                  },
                },
                y: {
                  beginAtZero: true,
                  border: { display: false },
                  grid: { color: COLORS.border },
                  ticks: { color: COLORS.muted, maxTicksLimit: 6, callback: value => num(value) },
                },
                // Only mounted while the overlay is on. A second axis drawn
                // against an empty dataset renders a bare 0-1 scale on the right
                // and reads as a broken chart.
                ...(hasFrequency
                  ? {
                      y1: {
                        position: 'right' as const,
                        beginAtZero: true,
                        border: { display: false },
                        // The impressions grid already rules the plot; a second
                        // set of lines at different intervals just makes noise.
                        grid: { display: false },
                        ticks: { color: COLORS.muted, maxTicksLimit: 6, callback: value => decimal(value) },
                      },
                    }
                  : {}),
              },
            }}
          />
        )}
      </Box>

      {showTable && series.length > 0 && !channelsPending && !channelsFailed && (
        <Box id={TABLE_ELEMENT_ID} sx={{ overflowX: 'auto' }}>
          <Table size='small' aria-label='Impressions over time, by date and channel'>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: 'relative',
                    width: colWidths.channel ? `${colWidths.channel}px` : undefined,
                    fontWeight: 600,
                    color: COLORS.muted,
                  }}
                >
                  Channel
                  <ColumnResizeHandle columnKey='channel' onResizeStart={startResize} />
                </TableCell>
                {table.dateLabels.map((label, i) => {
                  // Keyed by position, not the date label: the column set is as
                  // wide as the date range, so persisting by name would create
                  // one saved width per date ever viewed. A saved width here
                  // applies to "the nth date column" rather than a specific date.
                  const posKey = `date-${i}`;
                  return (
                    <TableCell
                      key={`${label}-${i}`}
                      align='right'
                      sx={{
                        position: 'relative',
                        width: colWidths[posKey] ? `${colWidths[posKey]}px` : undefined,
                        color: COLORS.muted,
                      }}
                    >
                      {label}
                      <ColumnResizeHandle columnKey={posKey} onResizeStart={startResize} />
                    </TableCell>
                  );
                })}
                <TableCell
                  align='right'
                  sx={{
                    position: 'relative',
                    width: colWidths.periodTotal ? `${colWidths.periodTotal}px` : undefined,
                    fontWeight: 600,
                    color: COLORS.muted,
                  }}
                >
                  Period total
                  <ColumnResizeHandle columnKey='periodTotal' onResizeStart={startResize} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {table.rows.map(row => (
                <TableRow key={row.name}>
                  <TableCell component='th' scope='row' sx={{ fontWeight: 600 }}>
                    {row.name}
                  </TableCell>
                  {row.cells.map((value, i) => (
                    <TableCell key={`${row.name}-${table.dateLabels[i]}-${i}`} align='right'>
                      {num(value)}
                    </TableCell>
                  ))}
                  <TableCell align='right' sx={{ fontWeight: 600 }}>
                    {num(row.periodTotal)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {table.truncated && (
            <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block', mt: 1 }}>
              {TABLE_TRUNCATION_NOTE}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default ImpressionsOverTime;
